import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { type ReactNode, useMemo } from 'react'
import type { RouterOutputs } from 'shared/api'
import { trpc } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { CompactTable, EmptyTableBody, SectionTitle, Stat, Stats } from 'shared/components/section'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { formatDayTime } from 'shared/lib/utils/date'
import { RegisterTotalsBeside } from 'shared/components/partner-registers/PartnerRegisters'
import { RemaindersDownload } from 'pages/dashboard/ui/RemaindersDownload'
import { MONEY_COLUMN, sum, WHOLE } from 'shared/lib/money'

export type MoneyOfEveryPlaceData = RouterOutputs['cashBags']['Money']
type Share = MoneyOfEveryPlaceData['centralCashbox']['partners'][number]
type Driver = MoneyOfEveryPlaceData['drivers'][number]
type Point = MoneyOfEveryPlaceData['points'][number]

/**
 * «Деньги» at the central cashbox (ADR-0036; ticket 30, user story 108): where the
 * partners' money is now — in the central cashbox, with the drivers, in each
 * Point's Cashbox (with the bag waiting there for a driver) — all folded by the
 * server from the Money Events, never a stored balance. The three totals on top,
 * then each place as a table. The cashier's and the admin's; an operator has no
 * such section.
 */
export function MoneyOfEveryPlace() {
	const { t } = useTranslate()
	const query = useQuery(trpc.cashBags.Money.queryOptions())
	if (!query.data) {
		return (
			<p className='text-sm text-muted-foreground'>
				{query.isError ? t('cashier.moneyFailed') : t('money.loading')}
			</p>
		)
	}
	return (
		<MoneyOverview
			data={query.data}
			download={<RemaindersDownload input={{ scope: 'network' }} testId='money-remainders-download' />}
			beside={<RegisterTotalsBeside />}
		/>
	)
}

/** Each partner's share of a place, one per line: the name, the amount. */
function Shares({ shares }: { shares: Share[] }) {
	const { t } = useTranslate()
	if (shares.length === 0) return <span className='text-muted-foreground'>—</span>
	return (
		<ul className='grid gap-0.5'>
			{shares.map((s) => (
				<li key={s.partnerId ?? 'none'} className='flex gap-3'>
					<span>{s.partnerName || t('money.noPartner')}</span>
					<span className='ml-auto tabular-nums'>{sum(s.cash)}</span>
				</li>
			))}
		</ul>
	)
}

const Dash = () => <span className='text-muted-foreground'>—</span>

/** The three kinds of place, as the server folded them. */
export function MoneyOverview({
	data,
	download,
	beside,
}: {
	data: MoneyOfEveryPlaceData
	download?: ReactNode
	beside?: ReactNode
}) {
	const { t } = useTranslate()
	const pointsTotal = data.points.reduce((total, p) => total + p.cash, 0)
	const driversTotal = data.drivers.reduce((total, d) => total + d.cash, 0)
	const central = data.centralCashbox

	const driverColumns = useMemo<ColumnDef<Driver>[]>(
		() => [
			{ id: 'driver', header: t('cashier.colDriver'), cell: ({ row }) => row.original.driverName },
			{
				id: 'bags',
				header: t('cashier.colBags'),
				cell: ({ row }) => (
					<ul className='grid gap-0.5'>
						{row.original.bags.map((b) => (
							<li key={b.cashBagId}>
								<span className='font-medium'>{b.pointCode}</span>{' '}
								<span className='text-muted-foreground tabular-nums'>{formatDayTime(b.sealedAt)}</span>
							</li>
						))}
					</ul>
				),
			},
			{ id: 'partners', header: t('cashier.colPartners'), cell: ({ row }) => <Shares shares={row.original.partners} /> },
			{ id: 'cash', header: t('cashier.colCash'), meta: MONEY_COLUMN, cell: ({ row }) => sum(row.original.cash) },
		],
		[t],
	)
	const drivers = useDataTable({
		data: data.drivers,
		columns: driverColumns,
		rowCount: data.drivers.length,
		...WHOLE,
		getRowId: (d) => d.driverId,
	}).table

	const pointColumns = useMemo<ColumnDef<Point>[]>(
		() => [
			{
				id: 'point',
				header: t('cashier.colPoint'),
				cell: ({ row }) => <span className='font-medium'>{row.original.pointCode}</span>,
			},
			{
				id: 'partners',
				header: t('cashier.colPartners'),
				cell: ({ row }) => <Shares shares={row.original.partners.filter((s) => s.cash !== 0)} />,
			},
			{
				id: 'waiting',
				header: t('cashier.colWaiting'),
				meta: MONEY_COLUMN,
				cell: ({ row }) =>
					row.original.waiting.length === 0 ? (
						<Dash />
					) : (
						<ul className='grid gap-0.5'>
							{row.original.waiting.map((b) => (
								<li key={b.cashBagId}>{sum(b.countedCash)}</li>
							))}
						</ul>
					),
			},
			{
				id: 'cash',
				header: t('cashier.colCash'),
				meta: MONEY_COLUMN,
				cell: ({ row }) =>
					row.original.cash === 0 ? <span className='text-muted-foreground'>0</span> : sum(row.original.cash),
			},
		],
		[t],
	)
	const points = useDataTable({
		data: data.points,
		columns: pointColumns,
		rowCount: data.points.length,
		...WHOLE,
		getRowId: (p) => p.pointId,
	}).table

	return (
		<div className='flex flex-col gap-6' data-testid='money-every-place'>
			<div className='flex flex-wrap items-start justify-between gap-4'>
				<Stats testId='money-totals'>
					<Stat label={t('cashier.centralTotal')} value={sum(central.cash)} valueTestId='money-central-cash' />
					<Stat label={t('cashier.driversTotal')} value={sum(driversTotal)} valueTestId='money-drivers-cash' />
					<Stat label={t('cashier.pointsTotal')} value={sum(pointsTotal)} valueTestId='money-points-cash' />
				</Stats>
				{download}
			</div>

			<section className='flex flex-col gap-3' data-testid='money-central'>
				<SectionTitle title={t('cashier.central')} hint={t('cashier.moneyExplain')} />
				<Stats>
					<Stat label={t('cashier.centralBagsCount')} value={String(central.bags)} />
					<Stat label={t('cashier.centralCounted')} value={sum(central.counted)} />
				</Stats>
				<CompactTable testId='money-central-partners'>
					<TableHeader>
						<TableRow>
							<TableHead>{t('money.colPartner')}</TableHead>
							<TableHead className='text-right'>{t('cashier.colCash')}</TableHead>
						</TableRow>
					</TableHeader>
					{central.partners.length === 0 ? (
						<EmptyTableBody columns={2}>{t('cashier.centralEmpty')}</EmptyTableBody>
					) : (
						<TableBody>
							{central.partners.map((s) => (
								<TableRow key={s.partnerId ?? 'none'}>
									<TableCell>{s.partnerName || t('money.noPartner')}</TableCell>
									<TableCell className='text-right tabular-nums'>{sum(s.cash)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					)}
				</CompactTable>
				{beside}
			</section>

			<section className='flex flex-col gap-2'>
				<SectionTitle title={t('cashier.drivers')} count={data.drivers.length} />
				<div data-testid='money-drivers'>
					<DataTable
						table={drivers}
						withPagination={false}
						emptyText={<span data-testid='money-drivers-none'>{t('cashier.driversNone')}</span>}
						row={{ attributes: (d) => ({ 'data-testid': `money-driver-${d.driverId}` }) }}
					/>
				</div>
			</section>

			<section className='flex flex-col gap-2'>
				<SectionTitle title={t('cashier.points')} count={data.points.length} />
				<div data-testid='money-points'>
					<DataTable
						table={points}
						withPagination={false}
						emptyText={t('cashier.pointsNone')}
						row={{ attributes: (p) => ({ 'data-testid': `money-point-${p.pointId}` }) }}
					/>
				</div>
			</section>
		</div>
	)
}
